import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  UploadedImageFile,
  UploadFileState,
  MediaUploadProps,
  filesRejectHandler,
  TrackingUrlArray,
  TrackingUrl,
  CrmUrlInputValue,
} from '@anyx/feature-crm/shared';
import { toast } from '@anyx/shared-ui';
import { BasicOption } from '@anyx/shared-utils';

import { useLineMessageStore } from './store';
import {
  FontSizeEnum,
  LineMessageFormFieldsProps,
  LineMessageBlockTypeWithImageUpload,
} from './types';

export function useBroadcastImageUpload({
  messageType,
  onImageUpdate,
}: {
  messageType: LineMessageBlockTypeWithImageUpload;
  onImageUpdate: (image?: UploadedImageFile) => void;
}): MediaUploadProps {
  const { t } = useTranslation('crm');
  const onImageUpload = useLineMessageStore((state) => state.onImageUpload);

  const updateImage = ({
    file,
    src,
    state,
  }: {
    file: File;
    src: string;
    state: UploadFileState;
  }) => {
    onImageUpdate({
      type: 'image',
      name: file.name,
      src,
      state,
      file,
    });
  };

  const uploadImage = async ({ file, fallbackSrc }: { file: File; fallbackSrc: string }) => {
    const remoteUrl = await onImageUpload?.(file, messageType);

    //Replace image src with uploaded url
    if (remoteUrl) {
      updateImage({ file, src: remoteUrl, state: 'remote' });
    } else {
      updateImage({ file, src: fallbackSrc, state: 'error' });
      toast.error(t('crm.mediaUpload.error.uploadImageFailure'));
    }
  };

  return {
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    maxSize: 1024 * 1024 * 2, // 2MB
    maxDimensions: { width: 1200, height: 1200 },
    onFilesAdded: async (files) => {
      const file = files[0];

      if (!file) return;

      const localUrl = URL.createObjectURL(file);

      // Set image to blob before uploading
      updateImage({ file, src: localUrl, state: 'local' });

      await uploadImage({
        file,
        fallbackSrc: localUrl,
      });
    },
    onFileRemove: () => {
      onImageUpdate(undefined);
    },
    onFilesReject: filesRejectHandler,
    onFileErrorRetry: async ({ file }) => {
      if (file) {
        const localUrl = URL.createObjectURL(file);
        await uploadImage({
          file,
          fallbackSrc: localUrl,
        });
      }
    },
  };
}

export const useBroadcastFontOptions = () => {
  const fontOptions: BasicOption<FontSizeEnum>[] = [
    {
      label: '14',
      value: FontSizeEnum.enum.sm,
    },
    {
      label: '16',
      value: FontSizeEnum.enum.md,
    },
    {
      label: '19',
      value: FontSizeEnum.enum.lg,
    },
    {
      label: '22',
      value: FontSizeEnum.enum.xl,
    },
    {
      label: '28',
      value: FontSizeEnum.enum.xxl,
    },
  ];

  return fontOptions;
};

interface UseBroadcastTrackingLinkReturn {
  insertTrackingLink: (url: string, shorten?: boolean) => Promise<TrackingUrl | undefined>;
  insertMultipleTrackingLink: (url: string[], shorten?: boolean) => Promise<TrackingUrlArray>;
  findTrackingLink: (url?: string) => CrmUrlInputValue;
}

export function useBroadcastTrackingLink(): UseBroadcastTrackingLinkReturn {
  const onGenerateTrackingLink = useLineMessageStore((state) => state.onGenerateTrackingLink);

  const { control } = useFormContext<LineMessageFormFieldsProps>();
  const urlLinkGroupId = useWatch({ control, name: 'urlLinkGroupId' });
  const { fields, append, update } = useFieldArray({
    control,
    name: 'trackingUrls',
  });

  const generateMultipleTrackingLink = async (
    url: string[],
    shorten?: boolean
  ): Promise<TrackingUrlArray> => {
    if (!onGenerateTrackingLink || !urlLinkGroupId) {
      return [];
    }

    return await onGenerateTrackingLink(url, {
      urlLinkGroupId,
      shorten,
    });
  };

  const generateTrackingLink = async (
    url: string,
    shorten?: boolean
  ): Promise<TrackingUrl | undefined> => {
    const ret = await generateMultipleTrackingLink([url], shorten);

    return ret?.[0];
  };

  const findTrackingLinkIndex = (originalUrl: string) => {
    return fields.findIndex((field) => field.originalUrl === originalUrl);
  };

  const appendOrUpdateTrackingUrlsField = (trackingUrl: TrackingUrl) => {
    const idx = findTrackingLinkIndex(trackingUrl.originalUrl);

    if (idx === -1) {
      append(trackingUrl);
    } else {
      const currentTrackingUrl = fields[idx] as TrackingUrl;

      if (currentTrackingUrl.shortUrl === undefined && trackingUrl.shortUrl !== undefined) {
        update(idx, trackingUrl);
      }
    }
  };

  const insertTrackingLink = async (url: string, shorten?: boolean) => {
    const trackingUrl = await generateTrackingLink(url, shorten);

    if (trackingUrl) {
      appendOrUpdateTrackingUrlsField(trackingUrl);
    }

    return trackingUrl;
  };

  const insertMultipleTrackingLink = async (urls: string[], shorten?: boolean) => {
    const trackingUrls = await generateMultipleTrackingLink(urls, shorten);

    trackingUrls.forEach((trackingUrl) => {
      appendOrUpdateTrackingUrlsField(trackingUrl);
    });

    return trackingUrls;
  };

  const findTrackingLink = (url?: string): CrmUrlInputValue => {
    const trackingUrl = fields.find((field) => field.trackingUrl === url);

    if (trackingUrl) {
      return {
        originalUrl: trackingUrl.originalUrl,
        displayUrl: trackingUrl.trackingUrl,
        input: '',
      };
    }

    return {
      originalUrl: '',
      displayUrl: '',
      input: url,
    };
  };

  return {
    insertTrackingLink,
    insertMultipleTrackingLink,
    findTrackingLink,
  };
}

export const useVariableUrlInput = () => {
  const variablePluginOptions = useLineMessageStore((store) => store.variablePluginOptions) || [];
  const variableUrlOptions = variablePluginOptions.filter((option) => option.type === 'url');
  const enableVariableUrl = variableUrlOptions.length > 0;

  return {
    variableUrlOptions,
    enableVariableUrl,
  };
};
